import React from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { Building2, History, LayoutDashboard, ListChecks, Loader2, ShieldCheck, UserCog, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/Brand";
import { LogoutButton } from "@/components/LogoutButton";

export const ROLE_LABELS: Record<string, string> = { admin: "Admin", account_manager: "Account manager", customer_service: "Customer service" };

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, roles: ["admin", "account_manager", "customer_service"], end: true },
  { to: "/admin/vendors", label: "Vendors", icon: Building2, roles: ["admin", "account_manager"] },
  { to: "/admin/event-types", label: "Event types", icon: ListChecks, roles: ["admin", "account_manager"] },
  { to: "/admin/attendees", label: "Attendees & consent", icon: Users, roles: ["admin", "customer_service"] },
  { to: "/admin/staff", label: "Company staff", icon: UserCog, roles: ["admin"] },
  { to: "/admin/audit", label: "Audit log", icon: History, roles: ["admin"] },
];

export const hasRole = (user: any, roles: string[]) => (user?.platform_roles || []).some((r: string) => roles.includes(r));

export default function AdminShell() {
  const { admin } = useAuth();
  if (admin === undefined) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!admin || admin.user.role !== "platform") return <Navigate to="/admin/login" replace />;
  const u = admin.user;
  return (
    <div className="min-h-screen bg-background lg:flex">
      <aside className="border-b bg-secondary text-white lg:h-screen lg:w-64 lg:shrink-0 lg:border-b-0">
        <div className="flex items-center justify-between px-5 py-4 [&_span]:!text-white [&_.text-primary]:!text-orange-400">
          <BrandMark />
        </div>
        <p className="flex items-center gap-1.5 px-5 pb-3 text-xs font-bold uppercase tracking-[0.2em] text-orange-400"><ShieldCheck className="h-4 w-4" /> Admin area</p>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col">
          {NAV.filter((n) => hasRole(u, n.roles)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 whitespace-nowrap rounded-sm px-3 py-2 text-sm ${isActive ? "bg-primary text-white" : "text-white/70 hover:bg-white/10"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4 text-xs text-white/60">
          <p className="truncate font-semibold text-white" data-testid="admin-current-email">{u.email}</p>
          <p data-testid="admin-current-roles">{u.platform_roles.map((r: string) => ROLE_LABELS[r]).join(" · ")}</p>
          <LogoutButton kind="admin" testid="admin-logout-button" className="mt-2 text-white/80 hover:bg-white/10 hover:text-white" />
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 sm:p-8"><Outlet /></main>
    </div>
  );
}
